import { memo, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { WordType } from '@goldcast/player-config/src/types/transcript-types';
import { getClipBroadcastId, isSplitsInTimelineEnabledPure } from '@goldcast/player-config/src/utils/clipUtils';
import { selectTranscript } from '@goldcast/player-config/src/selectors/contentMetadataStoreSelectors';
import useSplits, { SplitMode, shouldShowSplitsWarningDialog } from '../../hooks/useSplits';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import useEditorRestrictions from '@/hooks/useEditorRestrictions';
import TooltipV2 from '@/components/TooltipV2';
import Button from '@/components/atoms/Button/Button';
import Icon, { IconName } from '@/components/atoms/Icon';
import DynamicPopup from '@/components/atoms/DynamicPopup/DynamicPopup';
import ConfirmationDialog from '@/components/organisms/ConfirmationDialog';
import useDialog from '@/components/organisms/useDialog';
import { classnames } from '@/libs/utils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';

const SPLIT_DROPDOWN_OPTIONS: {
  mode: SplitMode;
  label: string;
  description: string;
  iconName: IconName;
}[] = [
  { mode: 'precise', label: 'Precise', description: 'Exact playhead position', iconName: 'IconCursorText' },
  { mode: 'word', label: 'Snap to word', description: 'Nearest word boundary', iconName: 'IconLetterCase' },
  {
    mode: 'sentence',
    label: 'Snap to sentence',
    description: 'Nearest sentence boundary',
    iconName: 'IconBlockquote'
  }
];

function SplitButton() {
  const { canUseSplits, reasonForDisabledSplits, performSplit } = useSplits();
  const { isBlocked } = useEditorRestrictions();
  const isSplitBlocked = !canUseSplits || isBlocked('timeline-cut');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { clipData, speakersAnalysis } = useClipsContext();
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isSplitsEnabledInTimeline = useMemo(
    () =>
      isSplitsInTimelineEnabledPure({
        clip: clipData,
        isSplitsTranslationEnabled: !!featureFlags[FeatureFlagKeys.Use_CL_Splits_Translations]
      }),
    [clipData, featureFlags]
  );
  const broadcastId = getClipBroadcastId(clipData) ?? '';

  const transcript = useContentMetadataStore(selectTranscript(broadcastId));
  const hasTranscriptWords = transcript.some(w => w.type !== WordType.Punctuation);

  const { isOpen: isWarningDialogOpen, openDialog: openWarningDialog, closeDialog: closeWarningDialog } = useDialog();
  const pendingSplitMode = useRef<SplitMode | null>(null);

  const triggerSplit = useCallback(
    (mode: SplitMode) => {
      if (!isSplitsEnabledInTimeline && shouldShowSplitsWarningDialog(clipData, speakersAnalysis)) {
        pendingSplitMode.current = mode;
        openWarningDialog();
      } else {
        performSplit(mode);
      }
    },
    [clipData, speakersAnalysis, isSplitsEnabledInTimeline, openWarningDialog, performSplit]
  );

  const handleConfirmSplit = useCallback(() => {
    if (pendingSplitMode.current !== null) {
      performSplit(pendingSplitMode.current, true);
      pendingSplitMode.current = null;
    }
    closeWarningDialog();
  }, [closeWarningDialog, performSplit]);

  const handlePrimaryClick = useCallback(() => {
    if (isSplitBlocked) {
      return;
    }
    triggerSplit(hasTranscriptWords ? 'word' : 'precise');
  }, [canUseSplits, hasTranscriptWords, triggerSplit]);

  const handleDropdownTriggerClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isSplitBlocked) {
        return;
      }
      setIsDropdownOpen(open => !open);
    },
    [canUseSplits]
  );

  const handleOptionSelect = useCallback(
    (mode: SplitMode) => {
      triggerSplit(mode);
      setIsDropdownOpen(false);
    },
    [triggerSplit]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isDropdownOpen]);

  let primaryButtonTooltip: string;
  if (isSplitBlocked) {
    primaryButtonTooltip = reasonForDisabledSplits;
  } else if (hasTranscriptWords) {
    primaryButtonTooltip = 'Split at word';
  } else {
    primaryButtonTooltip = 'Split';
  }

  return (
    <>
      <ConfirmationDialog
        isOpen={isWarningDialogOpen}
        onClose={closeWarningDialog}
        onConfirm={handleConfirmSplit}
        title="Enabling splits will disable your layouts"
        content="Adding a split will revert the layout to Original — speaker layouts aren't supported on split segments for this clip."
        confirmLabel="OK"
        cancelLabel="Cancel"
      />
      <div ref={containerRef} className="relative flex">
        <TooltipV2
          tooltipId="split-button-tooltip"
          tooltipContent={primaryButtonTooltip}
          noArrow
          place="top"
          positionStrategy="fixed"
          className="z-10"
          tooltipElement={
            <Button
              className={classnames(
                'flex size-[42px] items-center justify-center !p-0',
                hasTranscriptWords ? 'rounded-r-none border-r-0' : ''
              )}
              variation="checkbox"
              trackingId="split-button-primary"
              onClick={handlePrimaryClick}
              buttonSize="base"
              disabled={isSplitBlocked}
              onMouseDown={e => e.stopPropagation()}
            >
              <Icon name="IconScissors" size={15} className="timeline-icon" stroke={2.5} />
            </Button>
          }
        />
        {hasTranscriptWords && (
          <>
            <Button
              className={classnames(
                'flex size-[32px] h-[42px] items-center justify-center rounded-l-none !p-0',
                isSplitBlocked ? 'pointer-events-none opacity-50' : ''
              )}
              variation="checkbox"
              trackingId="split-button-dropdown-trigger"
              onClick={handleDropdownTriggerClick}
              buttonSize="base"
              disabled={isSplitBlocked}
              onMouseDown={e => e.stopPropagation()}
            >
              <Icon
                name={isDropdownOpen ? 'IconChevronUp' : 'IconChevronDown'}
                size={15}
                className="timeline-icon"
                stroke={2.5}
              />
            </Button>
            <DynamicPopup
              triggerRef={containerRef}
              isVisible={isDropdownOpen}
              preferredPosition="bottom"
              offset={4}
              viewportPadding={16}
            >
              <div
                className="min-w-[200px] rounded-lg border border-gray-200 bg-white py-2 shadow-lg"
                role="menu"
                aria-label="Split options"
                data-testid="split-button-dropdown"
              >
                {SPLIT_DROPDOWN_OPTIONS.map(({ mode, label, description, iconName }) => (
                  <button
                    key={mode}
                    data-testid={`split-option-${mode}`}
                    type="button"
                    className="flex w-full items-start gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => handleOptionSelect(mode)}
                    role="menuitem"
                  >
                    <Icon name={iconName} size={16} stroke={2} className="timeline-icon mt-0.5 shrink-0" />
                    <span className="flex flex-col">
                      <span className="font-medium">{label}</span>
                      <span className="text-xs text-gray-500">{description}</span>
                    </span>
                  </button>
                ))}
              </div>
            </DynamicPopup>
          </>
        )}
      </div>
    </>
  );
}

export default memo(SplitButton);
